import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { normalizeTableRowData, usePlatformTableListRowsTeam } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useSessionStorage } from '@uidotdev/usehooks';

import { setTablePerPageState, useEffectOnce } from 'shared/base';

import { TableStateContext } from './context';
import {
  DefaultPaginationModel,
  DefaultTableState,
  TableStateUrlParam,
} from '../../constants/tableState';
import { useAssetInventoryResourceCategories } from '../../hooks/useAssetInventoryResourceCategories';
import { useAssetInventorySchema } from '../../hooks/useAssetInventorySchema';
import { useSavedFilterManager } from '../../hooks/useSavedFilterManager';
import { useTableValidationContext } from '../../hooks/useTableValidationContext';
import { DataFilterString, DisplayColumns, TableState, TableStateValuesUnion } from '../../types';
import {
  dataFilterToApiFilter,
  decodeSearchParams,
  encodeSearchParams,
  getTableNameFromFilter,
  managePagination,
  manageSort,
  sortModelToApiSortProps,
  toTableState,
  constructNewTableState,
  manageDisplayColumns,
} from '../../utils/tableState';

interface Props {
  children: React.ReactNode;
  hideAllData: boolean;
  onNavigate(path: string, { replace }: { replace: boolean }): void;
  pathname: string;
  searchParams: URLSearchParams;
  tableId: string;
}

export const TableStateProvider: React.FC<Props> = ({
  children,
  hideAllData,
  onNavigate,
  pathname,
  searchParams,
  tableId,
}) => {
  const { currentMembership } = useBaseAuthenticated();
  const initialized = useRef(false);
  const [tableState, setTableState] = useState<TableState>(DefaultTableState);
  const { detailIds, filter, originalFilter, pagination: paginationModel, sort } = tableState;

  const {
    columns,
    defaultDisplayColumns,
    filterFields,
    hasEditableCustomColumns,
    isReady: schemaReady,
    tableName,
  } = useAssetInventorySchema(filter);

  const [displayColumnsStorage, persistDisplayColumns] = useSessionStorage(
    `${tableName}-displayColumns`,
    schemaReady ? defaultDisplayColumns : undefined,
  );
  const displayColumns = displayColumnsStorage ?? defaultDisplayColumns;

  const apiFilter = useMemo(() => dataFilterToApiFilter(filter, tableName), [filter, tableName]);

  const {
    isFetching: fetchingResourceCategories,
    resourceCategories,
    resourceCategoriesTotalCount,
  } = useAssetInventoryResourceCategories(hideAllData);

  const tableValidationContext = useTableValidationContext(
    columns,
    resourceCategories,
    filterFields,
  );

  const { savedFilterHasDeviated } = useSavedFilterManager(tableState, setTableState);

  useEffectOnce(() => {
    // Initializes the tableState from the query parameters
    const initialState = decodeSearchParams(searchParams, tableValidationContext, tableId);
    // Set default filter if none provided
    if (!initialState.filter) {
      initialState.filter = 'resource_category != ""';
    }
    setTableState(initialState);
    // Initialize display columns with schema defaults
    persistDisplayColumns(defaultDisplayColumns);
    initialized.current = true;
  }, schemaReady);

  const apiSortProps = useMemo(() => sortModelToApiSortProps(sort, tableName), [sort, tableName]);
  const selectColumns = [
    ...displayColumns,
    '_cq_id',
    '_cq_source_name',
    '_cq_platform_id',
    '_cq_sync_group_id',
  ];
  const { data, error, isLoading } = usePlatformTableListRowsTeam(
    currentMembership.team.name,
    tableName,
    {
      ...apiFilter,
      ...apiSortProps,
      page: paginationModel.page + 1,
      per_page: paginationModel.pageSize,
      select: [...new Set(selectColumns)],
    },
  );

  const rowCountRef = React.useRef(data?.data?.metadata?.total_count || 0);
  const { rowCount, rows } = useMemo(() => {
    if (data?.data?.metadata?.total_count !== undefined) {
      rowCountRef.current = data?.data?.metadata?.total_count;
    }

    return {
      rowCount: rowCountRef.current,
      rows: normalizeTableRowData(data?.data?.data),
    };
  }, [data]);

  const resetTableState = () => {
    setTableState(toTableState(undefined, tableValidationContext, tableId));
    onNavigate(pathname, { replace: true });
  };

  const overwriteFilter = (newFilter: DataFilterString) => {
    applyNewTableStateSegment(newFilter, TableStateUrlParam.Filter);
  };

  const setDisplayColumns = (toggleColumn: string) => {
    const newSegment = manageDisplayColumns(toggleColumn, new Set(displayColumns));
    persistDisplayColumns([...newSegment]);
  };

  const overwriteDisplayColumns = (newDisplayColumns: DisplayColumns) => {
    persistDisplayColumns([...newDisplayColumns]);
  };

  const setSort = (newSortModel: GridSortModel) => {
    const newSegment = manageSort(newSortModel);
    applyNewTableStateSegment(newSegment, TableStateUrlParam.Sort);
  };

  const setPagination = (newPaginationModel?: GridPaginationModel) => {
    const newSegment = managePagination(newPaginationModel, paginationModel);
    applyNewTableStateSegment(newSegment, TableStateUrlParam.Pagination);
    setTablePerPageState(tableId, newSegment.pageSize);
  };

  const setDetailIds = (detailIds?: string[]) => {
    applyNewTableStateSegment(detailIds, TableStateUrlParam.DetailIds);
  };

  const applySavedFilter = (filterId: string, newFilter: DataFilterString) => {
    const newTableName = getTableNameFromFilter(newFilter);
    const shouldResetSort = newTableName !== tableName;

    const newState = {
      ...tableState,
      filter: newFilter,
      originalFilter: { id: filterId, value: newFilter },
      pagination: DefaultPaginationModel,
      ...(shouldResetSort ? { sort: [] } : {}),
    };

    setTableState(newState);
  };

  const applyNewTableStateSegment = (
    newEntry: TableStateValuesUnion | undefined,
    type: TableStateUrlParam,
  ) => {
    setTableState((prevState) => {
      const newState = constructNewTableState({
        filter: prevState.filter,
        newEntry,
        tableState: prevState,
        type,
      });

      return newState;
    });
  };

  const removeSavedFilter = () => {
    const newState = { ...tableState, originalFilter: undefined };
    setTableState(newState);
  };

  const persistStateToQueryParams = useCallback(
    (newState: TableState) => {
      const newQuery = encodeSearchParams(newState, searchParams);
      const newURL = `${pathname}${newQuery ? `?${newQuery}` : ''}`;
      // Always replace for URL sync (push is handled elsewhere if needed)
      onNavigate(newURL, { replace: true });
    },
    [searchParams, pathname, onNavigate],
  );

  // Sync state to URL
  useEffect(() => {
    if (initialized.current) {
      persistStateToQueryParams(tableState);
    }
  }, [tableState, persistStateToQueryParams]);

  const resetParamSegment = (type: TableStateUrlParam) =>
    applyNewTableStateSegment(undefined, type);

  return (
    <TableStateContext.Provider
      value={{
        apiFilter,
        apiSortProps,
        applySavedFilter,
        columns,
        detailIds,
        displayColumns: new Set(displayColumns),
        error,
        fetchingResourceCategories,
        filter,
        filterFields,
        hasEditableCustomColumns,
        isLoading,
        originalFilter,
        overwriteDisplayColumns,
        overwriteFilter,
        paginationModel,
        removeSavedFilter,
        resetDisplayColumns: () => persistDisplayColumns(defaultDisplayColumns),
        resetFilter: () => resetParamSegment(TableStateUrlParam.Filter),
        resetSort: () => resetParamSegment(TableStateUrlParam.Sort),
        resetTableState,
        resourceCategories,
        resourceCategoriesTotalCount,
        rowCount,
        rows,
        savedFilterHasDeviated,
        setDetailIds,
        setDisplayColumns,
        setPagination,
        setSort,
        sort,
        tableName,
      }}
    >
      {children}
    </TableStateContext.Provider>
  );
};
